import { RootState } from "..";

import {
  breakoutGroupSelectors,
  classUserSelectors,
  createAppSelector,
  sectionUserSelectors,
} from "./base";
import { selectActiveClassSession, selectIsClassSessionVirtual } from "./class-session";
import { selectCurrentClassUser } from "./class-user";
import {
  selectIsRecordingAgent,
  selectUserIdsWhoAreVisibleInClass,
  selectUserIdsWhoCanParticipateInSection,
} from "./permissions";
import { selectCurrentSection, selectSectionUserIdToUserIdMap } from "./section";

export const selectClassUsersInVirtualClass = createAppSelector(
  [classUserSelectors.filterForCurrentSection, selectIsClassSessionVirtual],
  (classUsers, isClassSessionVirtual) =>
    isClassSessionVirtual ? classUsers.filter((cu) => cu.is_in_class_session) : [],
);

export const selectUserIdsInVirtualClass = createAppSelector(
  [selectClassUsersInVirtualClass, sectionUserSelectors.selectAll],
  (classUsers, sectionUsers) =>
    classUsers
      .map((classUser) => sectionUsers.find((cu) => cu.id === classUser.section_user_id)?.user_id)
      .filter((userId) => !!userId),
);

export const selectVirtualClassAvService = createAppSelector(
  [selectCurrentSection],
  (currentSection) => {
    if (!currentSection) {
      return null;
    }
    return currentSection.av_service;
  },
);

export const selectVirtualClassLicodeServerUrl = createAppSelector(
  [selectActiveClassSession],
  (currentClassSession) => {
    if (!currentClassSession) {
      return null;
    }
    return currentClassSession.licode_server_url;
  },
);

export const selectVirtualClassOpentokSessionId = createAppSelector(
  [selectActiveClassSession],
  (currentClassSession) => {
    if (!currentClassSession) {
      return null;
    }
    return currentClassSession.opentok_session_id;
  },
);
// If the user is in a breakout, return the screensharing user for that group.
// Otherwise, return the main class session screensharing user.
export const selectScreensharingClassUserId = createAppSelector(
  [selectActiveClassSession, selectCurrentClassUser, breakoutGroupSelectors.selectAll],
  (classSession, currentClassUser, breakoutGroups) => {
    const breakoutGroupId = currentClassUser?.breakout_group_id;
    if (breakoutGroupId) {
      const breakoutGroup = breakoutGroups.find((bg) => bg.id === breakoutGroupId);
      return breakoutGroup?.screensharing_class_user_id || null;
    }
    return classSession?.screensharing_class_user_id || null;
  },
);

export const selectIsScreensharingOn = createAppSelector(
  [selectScreensharingClassUserId],
  (screensharingClassUserId) => !!screensharingClassUserId,
);

export const selectCanStudentsScreenshare = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.can_students_screenshare || false,
);

export const selectViewingScreenshare = createAppSelector(
  [
    selectIsRecordingAgent,
    selectIsScreensharingOn,
    (state: RootState) => state.view.viewingScreenshare,
  ],
  (isRecordingAgent, isScreensharingOn, viewingScreenshare) =>
    (isRecordingAgent && isScreensharingOn) || viewingScreenshare,
);

export const selectCountStudentsInVirtualClass = createAppSelector(
  [selectUserIdsWhoCanParticipateInSection, selectUserIdsInVirtualClass],
  (studentUserIds, userIdsInVirtualClass) => {
    return studentUserIds.filter((userId) => userIdsInVirtualClass.includes(userId)).length;
  },
);

export const selectClassUsersWithIsVisibleInClassCapability = createAppSelector(
  [
    selectClassUsersInVirtualClass,
    selectUserIdsWhoAreVisibleInClass,
    selectSectionUserIdToUserIdMap,
  ],
  (classUsersInVirtualClass, visibleUserIds, sectionUserIdToUserIdMap) =>
    classUsersInVirtualClass.filter((classUser) => {
      const userId = sectionUserIdToUserIdMap.get(classUser.section_user_id);
      if (!userId) {
        return false;
      }
      return visibleUserIds.includes(userId);
    }),
);

export const selectClassUsersInMainCall = createAppSelector(
  [selectClassUsersWithIsVisibleInClassCapability],
  (classUsers) => classUsers.filter((classUser) => !classUser.breakout_group_id),
);
